/**
 * Layer Style Variations Configuration
 *
 * Defines the different style classes/variations for layers that have multiple styles
 * based on feature properties. This is used by the legend to show all variations.
 */

export interface StyleVariation {
  label: string;
  properties: Record<string, any>;
}

export const layerStyleVariations: Record<string, StyleVariation[]> = {
  // Desert susceptible areas - has 2 classes based on "classe" property
  areas_suscetiveis_desertificacao: [
    {
      label: "Áreas Semiáridas",
      properties: { classe: "Áreas Semiáridas" },
    },
    {
      label: "Áreas Subúmidas",
      properties: { classe: "Áreas Subúmidas" },
    },
  ],

  // Existing transmission lines - has 3 voltage levels based on "vn" property
  linhas_de_transmissao_base_existente: [
    {
      label: "69 kV (Em Operação)",
      properties: { vn: 69 },
    },
    {
      label: "230 kV (Em Operação)",
      properties: { vn: 230 },
    },
    {
      label: "500 kV (Em Operação)",
      properties: { vn: 500 },
    },
  ],

  // Planned transmission lines - has 2 voltage levels based on "vn" property
  linhas_de_transmissao_expansao_planejada: [
    {
      label: "230 kV (Planejada)",
      properties: { vn: 230 },
    },
    {
      label: "500 kV (Planejada)",
      properties: { vn: 500 },
    },
    {
      label: "600 kV (Planejada)",
      properties: { vn: 600 },
    },
  ],

  // Immediate regions of Pernambuco - grouped by Mesorregião
  // Gradient from dark green (coast) to hot orange (interior)
  regioes_imediatas_pe: [
    {
      label: "Metropolitana do Recife",
      properties: { cd_rgi: "260001" }, // Representative region: Recife
    },
    {
      label: "Mata Pernambucana",
      properties: { cd_rgi: "260002" }, // Representative region: Goiana - Timbaúba
    },
    {
      label: "Agreste Pernambucano",
      properties: { cd_rgi: "260009" }, // Representative region: Caruaru
    },
    {
      label: "Sertão Pernambucano",
      properties: { cd_rgi: "260013" }, // Representative region: Serra Talhada
    },
    {
      label: "São Francisco Pernambucano",
      properties: { cd_rgi: "260015" }, // Representative region: Petrolina
    },
  ],

  // Conjuntos elétricos — faixas do DGC do ano de referência.
  // As propriedades de exemplo alimentam a função de estilo para gerar a
  // amostra de cor de cada faixa na legenda.
  neoenergia_pe_conj_dgc: [
    { label: "DGC ≤ 0,9", properties: { DGC_2025: 0.5 } },
    { label: "DGC 0,9 – 1,0", properties: { DGC_2025: 0.95 } },
    { label: "DGC 1,0 – 1,1", properties: { DGC_2025: 1.05 } },
    { label: "DGC 1,1 – 1,2", properties: { DGC_2025: 1.15 } },
    { label: "DGC > 1,2", properties: { DGC_2025: 1.5 } },
    { label: "Sem dados", properties: { DGC_2025: null } },
  ],

  // Transnordestina (VALEC) — traçado contínuo para trechos concluídos e
  // tracejado para trechos ainda em obras
  "TRANSNORDESTINA-TRECHO-FERROVIÁRIO-VALEC-PE": [
    {
      label: "Obras Executadas",
      properties: { situacao: "Obras Executadas" },
    },
    {
      label: "Em Obras",
      properties: { situacao: "Em Obras" },
    },
  ],

  // Add more layers with variations here as needed
  // Example:
  // another_layer_name: [
  //   {
  //     label: "Class 1",
  //     properties: { type: "class1" },
  //   },
  //   {
  //     label: "Class 2",
  //     properties: { type: "class2" },
  //   },
  // ],
};

/**
 * Check if a layer has style variations
 */
export function hasStyleVariations(layerName: string): boolean {
  return layerName in layerStyleVariations;
}

/**
 * Get style variations for a layer
 */
export function getStyleVariations(layerName: string): StyleVariation[] | null {
  return layerStyleVariations[layerName] || null;
}
